import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShareLinkDto } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  SectionTitle,
  SuccessNotice,
} from '../components/ui';
import { formatDateTime } from '../lib/format';

export function ProfilePage(): React.JSX.Element {
  const { user, logout, refreshUser, hasRole, timeZone } = useAuth();
  const queryClient = useQueryClient();
  const isStaff = hasRole('TEACHER', 'ADMIN');

  const [form, setForm] = useState({
    name: user?.name ?? '',
    phone: user?.phone ?? '',
    email: user?.email ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const saveProfile = useMutation({
    mutationFn: () =>
      api.patch('/auth/me', {
        name: form.name,
        phone: form.phone || null,
        email: form.email || null,
      }),
    onSuccess: async () => {
      await refreshUser();
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось сохранить профиль');
    },
  });

  const requestLinkCode = useMutation({
    mutationFn: () => api.post<{ code: string }>('/auth/telegram/link-code'),
    onSuccess: (data) => setLinkCode(data.code),
  });

  const { data: shareLinks } = useQuery({
    queryKey: ['share-links'],
    queryFn: () => api.get<{ items: ShareLinkDto[] }>('/share-links'),
    enabled: isStaff,
  });

  const createShareLink = useMutation({
    mutationFn: () => api.post<{ link: ShareLinkDto }>('/share-links', { expiresInDays: 90 }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['share-links'] }),
  });

  const deactivateLink = useMutation({
    mutationFn: (id: string) => api.delete(`/share-links/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['share-links'] }),
  });

  const copy = async (link: ShareLinkDto): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Буфер обмена недоступен без https — ссылку всё равно видно на экране.
      setCopiedId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Профиль</h1>
        <p className="text-sm text-muted">Контакты, Telegram и ссылки на расписание</p>
      </div>

      <Card>
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}
          {saved && <SuccessNotice>Профиль сохранён</SuccessNotice>}

          {/* Придуманный логин менять нельзя, но знать его нужно: у сотрудников
              без email и телефона это единственное, чем они входят */}
          {user?.login && (
            <p className="rounded-xl bg-canvas px-3 py-2 text-sm">
              Ваш логин для входа: <b className="tabular-nums">{user.login}</b>
            </p>
          )}

          <Field label="Имя и фамилия">
            <Input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>

          <Field label="Телефон">
            <Input
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              type="tel"
              inputMode="tel"
            />
          </Field>

          <Field label="Email">
            <Input
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              type="email"
              inputMode="email"
            />
          </Field>

          <Button fullWidth isLoading={saveProfile.isPending} onClick={() => saveProfile.mutate()}>
            Сохранить
          </Button>
        </div>
      </Card>

      {/* Свой график инструктор ведёт сам, без обращения к администратору */}
      {hasRole('TEACHER') && (
        <Link to="/working-hours" className="block">
          <Card className="transition-colors hover:border-brand-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">Часы работы</p>
                <p className="text-sm text-muted">
                  В какие часы вы принимаете учеников — только их видит ученик при записи
                </p>
              </div>
              <span className="text-muted" aria-hidden>
                ›
              </span>
            </div>
          </Card>
        </Link>
      )}

      {/* Настройки школы: название и контакты, которые видит гость на главной */}
      {hasRole('ADMIN') && (
        <Link to="/settings" className="block">
          <Card className="transition-colors hover:border-brand-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">Настройки школы</p>
                <p className="text-sm text-muted">Название, телефон, телеграм</p>
              </div>
              <span className="text-muted" aria-hidden>
                ›
              </span>
            </div>
          </Card>
        </Link>
      )}

      {/* На телефоне раздела «Сотрудники» нет в нижней навигации — вход отсюда */}
      {hasRole('ADMIN') && (
        <Link to="/staff" className="block">
          <Card className="transition-colors hover:border-brand-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">Сотрудники</p>
                <p className="text-sm text-muted">
                  Инструкторы и администраторы
                  {user?.isSuperAdmin ? ' · вы суперадминистратор' : ''}
                </p>
              </div>
              <span className="text-muted" aria-hidden>
                ›
              </span>
            </div>
          </Card>
        </Link>
      )}

      <section>
        <SectionTitle>Telegram</SectionTitle>
        <Card>
          {user?.telegramId ? (
            <p className="text-sm">
              Аккаунт привязан
              {user.telegramUsername ? ` — @${user.telegramUsername}` : ''}. Уведомления о занятиях
              приходят в бот.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted">
                Привяжите Telegram, чтобы записываться на занятия и получать напоминания в боте.
              </p>
              {linkCode ? (
                <SuccessNotice>
                  Отправьте боту команду <b>/link {linkCode}</b>. Код действует 15 минут.
                </SuccessNotice>
              ) : (
                <Button
                  variant="secondary"
                  fullWidth
                  isLoading={requestLinkCode.isPending}
                  onClick={() => requestLinkCode.mutate()}
                >
                  Получить код привязки
                </Button>
              )}
            </div>
          )}
        </Card>
      </section>

      {isStaff && (
        <section>
          <SectionTitle
            action={
              <Button
                variant="ghost"
                isLoading={createShareLink.isPending}
                onClick={() => createShareLink.mutate()}
              >
                Создать
              </Button>
            }
          >
            Ссылки на расписание
          </SectionTitle>

          <p className="mb-3 text-sm text-muted">
            Отправьте ссылку ученику — он увидит ваше свободное время и оставит заявку без регистрации.
          </p>

          {!shareLinks || shareLinks.items.length === 0 ? (
            <EmptyState
              title="Ссылок пока нет"
              description="Создайте ссылку и отправьте её в чат ученикам."
            />
          ) : (
            <div className="space-y-2">
              {shareLinks.items.map((link) => (
                <Card key={link.id} className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {link.title ?? 'Ссылка на расписание'}
                      </p>
                      <p className="mt-0.5 break-all text-xs text-muted">{link.url}</p>
                      <p className="mt-1 text-xs text-muted">
                        {link.viewCount} просмотров · {link.requestCount} заявок
                        {link.expiresAt
                          ? ` · до ${formatDateTime(link.expiresAt, timeZone)}`
                          : ' · бессрочная'}
                      </p>
                    </div>
                    {!link.isActive && <Badge tone="neutral">выключена</Badge>}
                  </div>

                  {link.isActive && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button variant="secondary" onClick={() => void copy(link)}>
                        {copiedId === link.id ? 'Скопировано' : 'Копировать'}
                      </Button>
                      <Button variant="danger" onClick={() => deactivateLink.mutate(link.id)}>
                        Отключить
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>
      )}

      <Button variant="danger" fullWidth onClick={() => void logout()}>
        Выйти из аккаунта
      </Button>
    </div>
  );
}
